"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import dynamic from "next/dynamic";
import { RichText, type JSXConvertersFunction } from "@payloadcms/richtext-lexical/react";
import type { SerializedEditorState } from "@payloadcms/richtext-lexical/lexical";
import type { SerializedBlockNode } from "@payloadcms/richtext-lexical";
import { cn } from "@/utilities/core/cn";
import type { BannerBlock as BannerBlockData, CodeBlock as CodeBlockData, MediaBlock as MediaBlockData, CompetitorCardBlock as CompetitorCardBlockData, SportsMatchCardBlock as SportsMatchCardBlockData, GamificationCardBlock as GamificationCardBlockData, EmbedBlock as EmbedBlockData, ArticleReferenceBlock as ArticleReferenceBlockData, StandingsBlock as StandingsBlockData, MatchListBlock as MatchListBlockData, EntityLinkBlock as EntityLinkBlockData } from "@/payload-types";
import { BannerBlock } from "@/blocks/Banner/frontend/BannerBlock";
import { CodeBlock } from "@/blocks/Code/frontend/CodeBlock";
import { MediaBlockBlock } from "@/blocks/MediaBlock/frontend/MediaBlockBlock";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_BASIC } from "@/config/siteTypes";
import { buildEntityUrl } from "@/blocks/EntityLink/frontend/buildEntityUrl";
const CompetitorCardBlock = dynamic(() => import("@/blocks/CompetitorCard/frontend/CompetitorCardBlock").then(mod => mod.CompetitorCardBlock));
const SportsMatchCardBlock = dynamic(() => import("@/blocks/SportsMatchCard/frontend/SportsMatchCardBlock").then(mod => mod.SportsMatchCardBlock));
const GamificationCardBlock = dynamic(() => import("@/blocks/GamificationCard/frontend/GamificationCardBlock").then(mod => mod.GamificationCardBlock));
const EmbedBlock = dynamic(() => import("@/blocks/Embed/frontend/EmbedBlock").then(mod => mod.EmbedBlock));
const ArticleReferenceBlock = dynamic(() => import("@/blocks/ArticleReference/frontend/ArticleReferenceBlock").then(mod => mod.ArticleReferenceBlock));
const StandingsBlockComponent = dynamic(() => import("@/blocks/Standings/frontend/StandingsBlock").then(mod => mod.StandingsBlock));
const MatchListBlockComponent = dynamic(() => import("@/blocks/MatchList/frontend/MatchListBlock").then(mod => mod.MatchListBlock));
export interface PostBodyProps {
  /** The rich text content from Payload CMS (Lexical SerializedEditorState) */
  content?: SerializedEditorState | null;
  /** Optional additional className for the container */
  className?: string;
}

/**
 * PostBody component for rendering post content.
 *
 * This component renders rich text content with custom block components
 * for media, banners, and code blocks.
 */
export function PostBody(t0) {
  const $ = _c(12);
  const {
    content,
    className
  } = t0;
  const urlBuilder = useNavigation();
  const {
    siteType
  } = useTenantSettings();
  let t1;
  if ($[0] !== siteType || $[1] !== urlBuilder) {
    t1 = t2 => {
      const {
        defaultConverters
      } = t2;
      return {
        ...defaultConverters,
        blocks: {
          standings: _temp,
          matchList: _temp2,
          mediaBlock: _temp3,
          banner: _temp4,
          code: _temp5,
          competitorCard: _temp6,
          sportsMatchCard: _temp7,
          gamificationCard: _temp8,
          embed: _temp9,
          articleReference: _temp10
        },
        inlineBlocks: {
          entityLink: t3 => {
            const {
              node: node_9
            } = t3;
            const {
              label,
              entityId,
              entityType
            } = node_9.fields;
            if (siteType === SITE_TYPE_BASIC) {
              return <>{label}</>;
            }
            const href = buildEntityUrl(entityId, entityType, urlBuilder);
            if (!href) {
              return <>{label}</>;
            }
            return <a href={href}>{label}</a>;
          }
        }
      };
    };
    $[0] = siteType;
    $[1] = urlBuilder;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const jsxConverters = t1;
  if (!content) {
    return null;
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["prose-headings:mt-0", "prose-p:mt-0", "prose-blockquote:mt-0", "prose-figure:mt-0", "prose-pre:mt-0", "prose-ol:mt-0", "prose-ul:mt-0", "prose-li:mt-0", "prose-table:mt-0", "prose-hr:mt-0", "prose-img:mt-0", "prose-headings:mb-6", "prose-p:mb-6", "prose-blockquote:mb-6", "prose-figure:mb-6", "prose-pre:mb-6", "prose-ol:mb-6", "prose-ul:mb-6", "prose-li:mb-2", "prose-table:mb-6", "prose-hr:mb-6", "prose-img:mb-0", "[&>*:last-child]:mb-0"];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const proseSpacing = t2.join(" ");
  let t3;
  if ($[4] !== className) {
    t3 = cn("prose prose-lg max-w-[70ch] mx-auto", proseSpacing, className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== content || $[7] !== jsxConverters) {
    t4 = <RichText data={content} converters={jsxConverters} />;
    $[6] = content;
    $[7] = jsxConverters;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div data-element="post-body" className={t3}>{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function _temp10(t0) {
  const {
    node: node_8
  } = t0;
  return <ArticleReferenceBlock key={node_8.fields.id} block={node_8.fields} />;
}
function _temp9(t0) {
  const {
    node: node_7
  } = t0;
  return <EmbedBlock key={node_7.fields.id} block={node_7.fields} />;
}
function _temp8(t0) {
  const {
    node: node_6
  } = t0;
  return <GamificationCardBlock key={node_6.fields.id} block={node_6.fields} />;
}
function _temp7(t0) {
  const {
    node: node_5
  } = t0;
  return <SportsMatchCardBlock key={node_5.fields.id} block={node_5.fields} />;
}
function _temp6(t0) {
  const {
    node: node_4
  } = t0;
  return <CompetitorCardBlock key={node_4.fields.id} block={node_4.fields} />;
}
function _temp5(t0) {
  const {
    node: node_3
  } = t0;
  return <CodeBlock key={node_3.fields.id} block={node_3.fields} />;
}
function _temp4(t0) {
  const {
    node: node_2
  } = t0;
  return <BannerBlock key={node_2.fields.id} block={node_2.fields} />;
}
function _temp3(t0) {
  const {
    node: node_1
  } = t0;
  return <MediaBlockBlock key={node_1.fields.id} block={node_1.fields} />;
}
function _temp2(t0) {
  const {
    node: node_0
  } = t0;
  return <MatchListBlockComponent key={node_0.fields.id} block={node_0.fields} />;
}
function _temp(t0) {
  const {
    node
  } = t0;
  return <StandingsBlockComponent key={node.fields.id} block={node.fields} />;
}